/**
 * Route calculation utilities for commute times and directions
 */

import type { CommuteDestination, CommuteTime, TravelMode } from './commuteTypes';

/**
 * Calculate commute times for multiple travel modes
 */
export const calculateCommuteTimes = async (
  origin: { lat: number; lng: number },
  destination: CommuteDestination,
): Promise<Array<CommuteTime>> => {
  const directionsService = new window.google.maps.DirectionsService();
  const modes: Array<TravelMode> = ['driving', 'walking', 'transit', 'bicycling'];

  // Calculate routes for all modes in parallel
  const routePromises = modes.map((mode) => calculateRouteForMode(directionsService, origin, destination, mode));

  const results = await Promise.allSettled(routePromises);

  // Filter out failed requests and return successful ones
  return results
    .map((result, index) => ({
      result,
      mode: modes[index],
    }))
    .filter(({ result }) => result.status === 'fulfilled')
    .map(({ result, mode }) => (result as PromiseFulfilledResult<CommuteTime>).value);
};

/**
 * Calculate route for a specific travel mode
 */
const calculateRouteForMode = async (
  directionsService: google.maps.DirectionsService,
  origin: { lat: number; lng: number },
  destination: CommuteDestination,
  mode: TravelMode,
): Promise<CommuteTime> => {
  return new Promise((resolve, reject) => {
    const request: google.maps.DirectionsRequest = {
      origin: new window.google.maps.LatLng(origin.lat, origin.lng),
      destination: new window.google.maps.LatLng(destination.lat, destination.lng),
      travelMode: getTravelMode(mode),
      unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      avoidHighways: false,
      avoidTolls: false,
    };

    // Add departure time for transit
    if (mode === 'transit') {
      request.transitOptions = {
        departureTime: new Date(),
      };
    }

    directionsService.route(
      request,
      (result: google.maps.DirectionsResult | null, status: google.maps.DirectionsStatusString) => {
        if (status === 'OK' && result) {
          const route = result.routes[0];
          const leg = route.legs[0];

          resolve({
            mode,
            duration: leg.duration?.value || 0,
            distance: leg.distance?.value || 0,
            durationText: leg.duration?.text || 'Unknown',
            distanceText: leg.distance?.text || 'Unknown',
          });
        } else {
          reject(new Error(`Directions error for ${mode}: ${status}`));
        }
      },
    );
  });
};

/**
 * Get the complete route with polyline path
 */
export const getRouteWithPath = async (
  origin: { lat: number; lng: number },
  destination: CommuteDestination,
  mode: TravelMode = 'driving',
  signal?: AbortSignal,
): Promise<Array<google.maps.LatLng>> => {
  const directionsService = new window.google.maps.DirectionsService();

  return new Promise((resolve, reject) => {
    // Check if already aborted
    if (signal?.aborted) {
      reject(new DOMException('Operation was aborted', 'AbortError'));
      return;
    }

    // Set up abort listener
    const abortHandler = () => {
      reject(new DOMException('Operation was aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', abortHandler);

    const request: google.maps.DirectionsRequest = {
      origin: new window.google.maps.LatLng(origin.lat, origin.lng),
      destination: new window.google.maps.LatLng(destination.lat, destination.lng),
      travelMode: getTravelMode(mode),
      unitSystem: window.google.maps.UnitSystem.IMPERIAL,
    };

    directionsService.route(
      request,
      (result: google.maps.DirectionsResult | null, status: google.maps.DirectionsStatusString) => {
        // Clean up abort listener
        signal?.removeEventListener('abort', abortHandler);

        // Check abort before processing result
        if (signal?.aborted) {
          reject(new DOMException('Operation was aborted', 'AbortError'));
          return;
        }

        if (status === 'OK' && result) {
          const route = result.routes[0];
          const path: Array<google.maps.LatLng> = [];

          // Decode polyline path
          route.legs.forEach((leg: google.maps.DirectionsLeg) => {
            leg.steps.forEach((step: google.maps.DirectionsStep) => {
              path.push(...step.path);
            });
          });

          resolve(path);
        } else {
          reject(new Error(`Route path error: ${status}`));
        }
      },
    );
  });
};

/**
 * Convert our TravelMode to Google Maps TravelMode
 */
const getTravelMode = (mode: TravelMode): google.maps.TravelMode => {
  switch (mode) {
    case 'driving':
      return window.google.maps.TravelMode.DRIVING;
    case 'walking':
      return window.google.maps.TravelMode.WALKING;
    case 'transit':
      return window.google.maps.TravelMode.TRANSIT;
    case 'bicycling':
      return window.google.maps.TravelMode.BICYCLING;
    default:
      return window.google.maps.TravelMode.DRIVING;
  }
};

/**
 * Format duration in seconds to readable text
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return '< 1 min';
  }

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours} hr${hours > 1 ? 's' : ''}`;
  }

  return `${hours} hr ${remainingMinutes} min`;
};

/**
 * Format distance in meters to readable text
 */
export const formatDistance = (meters: number): string => {
  const miles = meters * 0.000621371; // Convert meters to miles

  if (miles < 0.1) {
    return '< 0.1 mi';
  } else if (miles < 1) {
    return `${miles.toFixed(1)} mi`;
  } else {
    return `${miles.toFixed(1)} mi`;
  }
};
